import { LogoutButton } from "@/components/logoutButton.component";
import { TaxpeerLogo } from "@/components/taxpeerlogo.component";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { type JWTPayload, SystemRole } from "@/lib/auth/auth.types";
import { useAuth } from "@/lib/contexts/auth.context";
import UserService from "@/lib/user.service";
import {
	Activity,
	ChevronDown,
	ClipboardList,
	FileText,
	Home,
	LayoutDashboard,
	Menu,
	User,
	Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

// This is the home link for the navbar, when the Taxpeer Logo is clicked.
const homeLink = {
	label: "Startseite",
	href: "/dashboard",
};

/**
 * Represents a navigation link with a label and a URL
 */
export interface NavLink {
	label: string;
	href: string;
	group?: "user" | "admin";
	icon?: string;
}

/**
 * Maps icon string names to lucide-react icon components
 */
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
	Home,
	LayoutDashboard,
	Users,
	FileText,
	ClipboardList,
};

/**
 * Gets the icon component for a given icon name
 */
const getIcon = (iconName?: string) => {
	if (!iconName) return null;
	const IconComponent = iconMap[iconName];
	return IconComponent ? <IconComponent className="h-4 w-4 mr-2" /> : null;
};

/**
 * This hook fetches the navigation links for the user
 * @returns The navigation links for the user
 */
/**
 * This hook fetches the navigation links for the user
 * @returns The navigation links for the user
 */
const useNavLinks = () => {
	const [navLinks, setNavLinks] = useState<NavLink[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const { organizations } = useAuth(); // Add this to get organizations from auth context

	useEffect(() => {
		const userService = UserService.getInstance();

		/**
		 * Fetch the navigation links for the user
		 */
		const fetchNavLinks = async () => {
			try {
				setIsLoading(true); // Set loading to true when refetching
				const links = await userService.getNavLinks();
				setNavLinks(links);
			} catch (error) {
				console.error("Failed to fetch navigation links:", error);
				setNavLinks([]);
			} finally {
				setIsLoading(false);
			}
		};

		fetchNavLinks();
	}, [organizations]); // Add organizations as dependency to refetch when they change

	return { navLinks, isLoading };
};

/**
 * Those will only be shown if the user is a platform manager
 * @param user - The user to check
 * @param handleNavigation - Function to handle navigation after refreshing organizations
 * @returns The platform manager links
 */
function platformManagerLinks(user: JWTPayload) {
	if (user.systemRoles.includes(SystemRole.PLATFORM_MANAGER)) {
		return (
			<>
				<DropdownMenuItem asChild>
					<Link to="/platformManager/dashboard">
						<Activity className="h-4 w-4 mr-2" />
						Plattformverwaltung
					</Link>
				</DropdownMenuItem>
			</>
		);
	}
}

/**
 * This component is the navbar of the application.
 * If the size of the screen is less than 1024px, a hamburger menu is shown instead of direct link buttons.
 * The links for the navigation are based on the user's role and are fetched from the NavigationService.
 *
 * If the links are still loading, a loading state is shown.
 *
 * It will show the user's display name.
 */
export const Navbar = () => {
	const { navLinks, isLoading } = useNavLinks();
	const location = useLocation();
	const navigate = useNavigate();
	const { user, refreshOrganizations } = useAuth();

	/**
	 * Handles navigation and if necessary refresh the organizations
	 * - It will only refresh the organizations if the path is "/orga-korrektur" or "/orga-admin"
	 * @param path - The path to navigate to
	 * @param e - The click event
	 */
	const handleNavigation = async (path: string, e: React.MouseEvent) => {
		e.preventDefault();
		if (
			path === "/orga-korrektur/dashboard" ||
			path === "/orga-admin/dashboard"
		) {
			await refreshOrganizations();
		}
		navigate(path);
	};

	if (!user) {
		return null;
	}

	/**
	 * Renders navigation links for desktop (horizontal, no group headers)
	 */
	const renderDesktopNavLinks = () => {
		if (isLoading) {
			return (
				<Button variant="ghost" className="opacity-50" key="loading-1" disabled>
					Menü wird geladen...
				</Button>
			);
		}

		// Group links by their group property for visual separation
		const userLinks = navLinks.filter((link) => link.group === "user");
		const adminLinks = navLinks.filter((link) => link.group === "admin");

		return (
			<>
				{/* Admin links on the left */}
				{adminLinks.map((link) => (
					<Button
						asChild
						variant="ghost"
						size="sm"
						className={`${location.pathname === link.href ? "bg-accent" : ""} gap-0`}
						key={link.href}
					>
						<Link
							to={link.href}
							onClick={(e) => handleNavigation(link.href, e)}
						>
							{getIcon(link.icon)}
							{link.label}
						</Link>
					</Button>
				))}
				{/* Spacer to push user links to the right */}
				{userLinks.length > 0 && adminLinks.length > 0 && (
					<div className="flex-1" />
				)}
				{/* User links on the right */}
				{userLinks.map((link) => (
					<Button
						asChild
						variant="ghost"
						size="sm"
						className={`${location.pathname === link.href ? "bg-accent" : ""} gap-1`}
						key={link.href}
					>
						<Link
							to={link.href}
							onClick={(e) => handleNavigation(link.href, e)}
							className="gap-1"
						>
							{getIcon(link.icon)}
							{link.label}
						</Link>
					</Button>
				))}
			</>
		);
	};

	/**
	 * Renders the navigation links grouped by category (for mobile)
	 * @returns The navigation links
	 */
	const renderNavLinks = () => {
		if (isLoading) {
			return (
				<>
					<Button
						variant="ghost"
						className="w-full justify-start opacity-50"
						key="loading-1"
						disabled
					>
						Menü wird geladen...
					</Button>
				</>
			);
		}

		// Group links by their group property
		const userLinks = navLinks.filter((link) => link.group === "user");
		const adminLinks = navLinks.filter((link) => link.group === "admin");

		return (
			<>
				{/* User Section */}
				{userLinks.length > 0 && (
					<>
						<div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
							Benutzerbereich
						</div>
						{userLinks.map((link) => (
							<Button
								asChild
								variant="ghost"
								className={`w-full justify-start ${
									location.pathname === link.href ? "bg-accent" : ""
								}`}
								key={link.href}
							>
								<Link
									to={link.href}
									onClick={(e) => handleNavigation(link.href, e)}
								>
									{getIcon(link.icon)}
									{link.label}
								</Link>
							</Button>
						))}
					</>
				)}

				{/* Admin Section */}
				{adminLinks.length > 0 && (
					<>
						{userLinks.length > 0 && (
							<div className="my-2 border-t border-border" />
						)}
						<div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
							Administration
						</div>
						{adminLinks.map((link) => (
							<Button
								asChild
								variant="ghost"
								className={`w-full justify-start ${
									location.pathname === link.href ? "bg-accent" : ""
								}`}
								key={link.href}
							>
								<Link
									to={link.href}
									onClick={(e) => handleNavigation(link.href, e)}
								>
									{getIcon(link.icon)}
									{link.label}
								</Link>
							</Button>
						))}
					</>
				)}
			</>
		);
	};

	return (
		<header className="flex h-16 w-full shrink-0 items-center px-4 md:px-6 border-b bg-card">
			<Sheet>
				<SheetTrigger asChild>
					<Button variant="ghost" size="iconLg" className="mr-2 lg:hidden">
						<Menu className="h-6 w-6" />
						<span className="sr-only">Toggle navigation menu</span>
					</Button>
				</SheetTrigger>
				<SheetContent side="left">
					<div className="flex flex-col gap-1 py-6">{renderNavLinks()}</div>
				</SheetContent>
			</Sheet>
			<Link
				to={homeLink.href}
				className="mr-6 flex"
				onClick={(e) => handleNavigation(homeLink.href, e)}
			>
				<TaxpeerLogo width={125} />
				<span className="sr-only">Taxpeer</span>
			</Link>
			<nav className="ml-auto flex items-center gap-3">
				<div className="hidden lg:flex gap-2 items-center">
					{renderDesktopNavLinks()}
				</div>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<div className="flex items-center">
							<Avatar className="cursor-pointer">
								<AvatarFallback>
									{user?.displayName?.charAt(0).toUpperCase() || "TP"}
								</AvatarFallback>
							</Avatar>
							<ChevronDown className="h-4 w-4 " />
						</div>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						{platformManagerLinks(user)}
						<DropdownMenuItem asChild>
							<Link
								to="/profile"
								onClick={(e) => handleNavigation("/profile", e)}
							>
								<User className="h-4 w-4 mr-2" />
								Mein Profil
							</Link>
						</DropdownMenuItem>
						<DropdownMenuItem>
							<LogoutButton />
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</nav>
		</header>
	);
};
